import { z } from 'zod';
import { PERMISSION_CATALOG, PermissionCodeSchema, RoleRefSchema } from '../rbac/index.js';
import { nonEmptyPatch } from './_patch.js';

/**
 * @aiPurpose full row projection of a Role for the admin `/roles` list +
 *   edit drawer; `RoleRef` extended with archived/timestamps/description.
 * @aiInvariant `createdAt` / `updatedAt` are ISO-8601 with offset; serialised
 *   from Prisma's `DateTime` columns. The server-side `Role.permsVersion`
 *   column is intentionally NOT projected here: the optimistic-concurrency
 *   fence travels on `GetRolePermissionsResponseSchema` instead, so this
 *   strict row schema stays focused on the row-edit surface and the
 *   permission-grid query owns the version it round-trips on PUT.
 * @aiSecurity gated by `roles.read` (read) / `roles.manage` (mutations).
 */
export const RoleEntitySchema = RoleRefSchema.extend({
  description: z.string().nullable(),
  archived: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
}).strict();

/**
 * @aiPurpose typed projection of `RoleEntitySchema`.
 * @aiInvariant see `RoleEntitySchema`.
 * @aiSecurity see `RoleEntitySchema`.
 */
export type RoleEntity = z.infer<typeof RoleEntitySchema>;

/**
 * @aiPurpose response shape for `GET /api/roles` — list of `RoleEntity` rows
 *   used by the admin `/roles` page (description / archived / timestamps) AND
 *   by user CRUD UIs that only consume the `RoleRef` projection. `RoleEntity`
 *   is a strict superset of `RoleRef`, so the (b)-era consumer keeps reading
 *   the same id/name/displayName/isSystem fields it always did.
 * @aiInvariant default ordering is system-roles first then alphabetical by
 *   `name`. The `?archived=` query param controls inclusion of archived rows
 *   (default `false`); the schema itself imposes no archived-vs-active filter.
 * @aiSecurity gated by the `roles.read` permission code (see PERMISSION_CATALOG).
 */
export const GetRolesResponseSchema = z
  .object({
    items: z.array(RoleEntitySchema),
  })
  .strict();

/**
 * @aiPurpose typed projection of `GetRolesResponseSchema`.
 * @aiInvariant see `GetRolesResponseSchema`.
 * @aiSecurity see `GetRolesResponseSchema`.
 */
export type GetRolesResponse = z.infer<typeof GetRolesResponseSchema>;

/**
 * @aiPurpose query string for `GET /api/roles` — the optional `archived`
 *   selector for the admin `/roles` page.
 * @aiInvariant default is `false` (preserves pre-(c) behaviour for users-page
 *   role selectors). `all` is the only way to retrieve archived rows alongside
 *   active ones in a single call. `.strict()` blocks unknown keys.
 * @aiSecurity see `GetRolesResponseSchema`.
 */
export const GetRolesQuerySchema = z
  .object({
    archived: z.enum(['true', 'false', 'all']).default('false'),
  })
  .strict();

/**
 * @aiPurpose typed projection of `GetRolesQuerySchema`.
 * @aiInvariant see `GetRolesQuerySchema`.
 * @aiSecurity see `GetRolesQuerySchema`.
 */
export type GetRolesQuery = z.infer<typeof GetRolesQuerySchema>;

/**
 * @aiPurpose request body for `POST /api/roles` — create a custom role.
 * @aiInvariant `name` is the immutable machine identifier; the regex enforces
 *   `^[a-z][a-z0-9_]{2,31}$` (no dot — keeps roles distinguishable from
 *   `<resource>.<action>` permission codes). `displayName` is 1..64 chars.
 *   Created role always has `isSystem=false, archived=false`.
 * @aiSecurity gated by `roles.manage`; populates `roles` table.
 */
export const CreateRoleInputSchema = z
  .object({
    name: z
      .string()
      .min(3)
      .max(32)
      .regex(/^[a-z][a-z0-9_]{2,31}$/),
    displayName: z.string().min(1).max(64),
    description: z.string().min(1).max(280).optional(),
  })
  .strict();

/**
 * @aiPurpose typed projection of `CreateRoleInputSchema`.
 * @aiInvariant see `CreateRoleInputSchema`.
 * @aiSecurity see `CreateRoleInputSchema`.
 */
export type CreateRoleInput = z.infer<typeof CreateRoleInputSchema>;

/**
 * @aiPurpose request body for `PATCH /api/roles/:id` — rename / re-describe.
 * @aiInvariant `name` is intentionally absent from this schema (machine id is
 *   immutable post-creation); supplying it triggers `.strict()` rejection.
 *   `description` is nullable to support clearing. Empty `{}` returns the
 *   canonical `'patch must contain at least one field'` issue (single-issue
 *   contract via `nonEmptyPatch`); unknown-only bodies surface as
 *   `unrecognized_keys` without the secondary empty-patch leak.
 * @aiSecurity gated by `roles.manage`.
 */
export const PatchRoleInputSchema = nonEmptyPatch(
  z
    .object({
      displayName: z.string().min(1).max(64).optional(),
      description: z.string().min(1).max(280).nullable().optional(),
    })
    .strict(),
);

/**
 * @aiPurpose typed projection of `PatchRoleInputSchema`.
 * @aiInvariant see `PatchRoleInputSchema`.
 * @aiSecurity see `PatchRoleInputSchema`.
 */
export type PatchRoleInput = z.infer<typeof PatchRoleInputSchema>;

/**
 * @aiPurpose request body for `PUT /api/roles/:id/permissions` — batch
 *   overwrite of the role's permission set.
 * @aiInvariant `permissions` is deduplicated at parse time via Set transform —
 *   service layer receives a unique array. `.refine()` caps array length at
 *   the catalog cardinality so a malformed payload cannot blow up DB inserts.
 *   Unknown codes (not in `PermissionCodeSchema.options`) → ZodError → 400.
 *   Co-grant rules (`users.manage` requires `roles.read`) are enforced in
 *   the service, not here — schema captures shape, service captures policy.
 *   `ifVersion` is the optimistic-concurrency fence: the server CAS-updates
 *   `Role.permsVersion` from `ifVersion` → `ifVersion + 1`; a no-row-match
 *   raises `ROLE_PERMISSIONS_STALE` (HTTP 409). Required: a payload missing
 *   `ifVersion` is rejected at the schema layer (400 VALIDATION_ERROR) so
 *   the fence is unconditional — there is no path where a write proceeds
 *   without committing a captured snapshot to the row.
 * @aiSecurity gated by `roles.manage`; full replace of `role_permissions`
 *   for the role in a single transaction.
 */
export const SetRolePermissionsInputSchema = z
  .object({
    permissions: z
      .array(PermissionCodeSchema)
      .transform((arr) => Array.from(new Set(arr)))
      .refine((arr) => arr.length <= PERMISSION_CATALOG.length, {
        message: 'permissions exceed catalog cardinality',
      }),
    ifVersion: z.number().int().nonnegative(),
  })
  .strict();

/**
 * @aiPurpose typed projection of `SetRolePermissionsInputSchema`.
 * @aiInvariant see `SetRolePermissionsInputSchema`.
 * @aiSecurity see `SetRolePermissionsInputSchema`.
 */
export type SetRolePermissionsInput = z.infer<typeof SetRolePermissionsInputSchema>;

/**
 * @aiPurpose response shape for `GET /api/roles/:id/permissions` and
 *   `PUT /api/roles/:id/permissions` — the current code set after the call.
 * @aiInvariant for ADMIN, the endpoint resolves the literal full catalog
 *   (NOT the `'ALL'` sentinel) so the UI grid renders every code as checked.
 *   `permsVersion` is the monotonic counter on the underlying `Role` row
 *   (server-bumped on every successful permission write or grant migration);
 *   the SPA captures it on GET and round-trips it as `ifVersion` on PUT
 *   to trip `ROLE_PERMISSIONS_STALE` if a concurrent write or grant
 *   migration advanced the value behind its back. The PUT response carries
 *   the newly-bumped value so the SPA's TanStack cache stays in sync
 *   without a follow-up GET. Required: every successful read + write surface
 *   carries the version, and a server response missing it would fail wire
 *   validation (defensive — the server unconditionally emits it).
 * @aiSecurity gated by `roles.read` (GET) / `roles.manage` (PUT).
 */
export const GetRolePermissionsResponseSchema = z
  .object({
    items: z.array(PermissionCodeSchema),
    permsVersion: z.number().int().nonnegative(),
  })
  .strict();

/**
 * @aiPurpose typed projection of `GetRolePermissionsResponseSchema`.
 * @aiInvariant see `GetRolePermissionsResponseSchema`.
 * @aiSecurity see `GetRolePermissionsResponseSchema`.
 */
export type GetRolePermissionsResponse = z.infer<typeof GetRolePermissionsResponseSchema>;

/**
 * @aiPurpose response wrapper for `GET /api/roles/:id` (admin detail view) —
 *   the full `RoleEntity` shape.
 * @aiInvariant single-item envelope keeps the contract stable if future
 *   sub-issues add side-channels (e.g. cached permission count).
 * @aiSecurity gated by `roles.read`.
 */
export const GetRoleResponseSchema = z.object({ role: RoleEntitySchema }).strict();
export type GetRoleResponse = z.infer<typeof GetRoleResponseSchema>;

/**
 * @aiPurpose response shape for `POST /api/roles/:id/archive` and
 *   `POST /api/roles/:id/restore` — idempotent envelope reporting whether
 *   the row's archived flag actually changed.
 * @aiInvariant `changed=false` when the row was already in the requested
 *   archived state; `role` always carries the post-operation row.
 * @aiSecurity gated by `roles.manage`.
 */
export const RoleArchiveToggleResponseSchema = z
  .object({
    changed: z.boolean(),
    role: RoleEntitySchema,
  })
  .strict();
export type RoleArchiveToggleResponse = z.infer<typeof RoleArchiveToggleResponseSchema>;

/**
 * @aiPurpose lightweight User projection for the role-detail "Пользователи"
 *   drawer tab; never includes auth fields.
 * @aiInvariant `id` is a UUID; `email` lowercase-validated; `active` mirrors
 *   `User.active`. `name` is the display label.
 * @aiSecurity gated by `roles.read`.
 * @aiPii email + name are PII; consumer must respect log-redaction rules
 *   when forwarding the value off-system.
 */
export const UserSummarySchema = z
  .object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().min(1),
    active: z.boolean(),
  })
  .strict();
export type UserSummary = z.infer<typeof UserSummarySchema>;

/**
 * @aiPurpose response shape for `GET /api/roles/:id/users` — lazy-load
 *   for the admin role-edit drawer tab.
 * @aiInvariant ordering is by `email ASC`; archived users included by default
 *   (caller filters on `active` if needed).
 * @aiSecurity gated by `roles.read`.
 * @aiPii every item carries email + name.
 */
export const GetRoleUsersResponseSchema = z
  .object({
    items: z.array(UserSummarySchema),
  })
  .strict();
export type GetRoleUsersResponse = z.infer<typeof GetRoleUsersResponseSchema>;
